import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import PaginationA from 'components/Pagination/PaginationA';
import CardListMessageA from 'components/CardListMessage/CardListMessageA';
import FontIcon from 'components/FontIcon';
import classNames from 'classnames';
import classes from './ResultTableA.module.scss';

const ResultTableA = (props) => {
  const {
    results,
    onPageChange,
    activePage,
    sortConfig,
    onSortChange,
    loading,
  } = props;

  const containerRef = useRef(null);

  const sortByField = (fieldName) => {
    const { field, order } = sortConfig;
    if (field === fieldName) {
      if (order === 0) return onSortChange({ field, order: 1 });
      return onSortChange({ field, order: 0 });
    }
    return onSortChange({ field: fieldName, order: 0 });
  };

  const addSortCaret = (fieldName) => {
    const { field, order } = sortConfig;
    if (field === fieldName) {
      if (order === 0) {
        return (
          <FontIcon icon='fa fa-caret-up' className={classes.caret} />
        );
      }
      return (
        <FontIcon icon='fa fa-caret-down' className={classes.caret} />
      );
    }
    return null;
  };

  const renderTableHeader = (fieldName, spanText, isCentered, sticky) => (
    <th className={classNames(
      classes.tableHeader,
      isCentered ? classes.centered : null,
      sticky ? classes.stickyCol : null,
    )}
    >
      <button
        aria-label={`${spanText}: activate to sort column`}
        onClick={() => sortByField(fieldName)}
        type='button'
      >
        <span>{spanText}</span>
        {addSortCaret(fieldName)}
      </button>
    </th>
  );

  const renderBodyRow = (item = {}) => {
    const { firstName, lastName, suffix, acronym: designation, acronymMark, city, state, zipCode } = item;
    return (
      <tr
        className={classes.tableBodyRow}
        key={`Designee-Info-${firstName}-${lastName}-${designation}-${city}-${state}-${zipCode}`}
      >
        <td className={classNames(classes.tableCell, classes.colored, classes.leftAligned, classes.stickyCol)}>
          <span className={classes.text}>{`${[firstName, lastName, suffix].join(' ')}`}</span>
        </td>
        <td className={classNames(classes.tableCell, classes.centered)}>
          <span
            className={classes.text}
            dangerouslySetInnerHTML={{ __html: `${designation}${acronymMark || ''}` }}
          />
        </td>
        <td className={classNames(classes.tableCell, classes.centered)}>
          <span className={classes.text}>{city}</span>
        </td>
        <td className={classNames(classes.tableCell, classes.centered)}>
          <span className={classes.text}>{state}</span>
        </td>
        <td className={classNames(classes.tableCell, classes.centered)}>
          <span className={classes.text}>{zipCode}</span>
        </td>
      </tr>
    );
  };

  const renderInfoMessage = (text) => (
    <tr>
      <td className={classes.noResults}>
        <span className={classes.text}>{text}</span>
      </td>
    </tr>
  );

  const renderLoadingMessage = () => (
    <tbody className={classes.tableBodyContainer}>
      {renderInfoMessage('Loading data...')}
    </tbody>
  );

  const renderItems = (items) => (
    <tbody className={classes.tableBodyContainer}>
      {items.length > 0
        ? items.map((item) => renderBodyRow(item))
        : renderInfoMessage('No data available in table')}
    </tbody>
  );

  const renderResults = (data = {}, loadingFlag) => {
    const { designation = {} } = data.public || {};
    const { forDesignees = {} } = designation || {};
    const { totalRecords, designees: items = [] } = forDesignees || {};
    return (
      <>
        <span className={classes.tableTitle}>Advisor Search results</span>
        <CardListMessageA
          itemsPerPage={items.length > 0 ? 10 : -1}
          totalItemsLength={totalRecords}
          itemsOnPageLength={items.length}
          categoryLabel='results'
        />
        <div className={classes.tableContainer}>
          <table>
            <colgroup>
              <col className={classes.firstColumn} />
              <col className={classes.secondColumn} />
              <col className={classes.thirdColumn} />
              <col className={classes.fourthColumn} />
              <col className={classes.fifthColumn} />
            </colgroup>
            <thead className={classes.tableHeaderContainer}>
              <tr className={classes.tableHeaderRow}>
                {renderTableHeader('lastName', 'Name', false, true)}
                {renderTableHeader('acronym', 'Designation', true)}
                {renderTableHeader('city', 'City', true)}
                {renderTableHeader('state', 'State', true)}
                {renderTableHeader('zipCode', 'Zip Code', true)}
              </tr>
            </thead>
            {!loadingFlag && renderItems(items)}
            {loadingFlag && renderLoadingMessage()}
          </table>
        </div>
        <PaginationA
          containerRef={containerRef}
          pageCount={Math.ceil(totalRecords / 10)}
          setActivePage={onPageChange}
          activePage={activePage}
        />
      </>
    );
  };

  return (
    <div ref={containerRef} className={classes.container}>
      {(!isEmpty(results) || loading) && renderResults(results, loading)}
    </div>
  );
};

ResultTableA.propTypes = {
  results: PropTypes.object,
  onPageChange: PropTypes.func,
  activePage: PropTypes.number,
  sortConfig: PropTypes.object,
  onSortChange: PropTypes.func,
  loading: PropTypes.bool,
};

export default ResultTableA;
